/**
 * 배출계수 components[] 변환/유도 헬퍼
 * 문서: [중요] 배출계수 체계화 및 API 정리, 260314-FEAT-ApplyCalorificFactor
 * - CO2/CH4/N2O/SF6 + HFC·PFC 종별 componentCode 행 + 열량계수(NCV, GCV) → components[]
 */

const BASKET_GAS_CODES = ["CO2", "CH4", "N2O", "HFCs", "PFCs", "SF6"];
const FOUR_FLAT_GAS_CODES = ["CO2", "CH4", "N2O", "SF6"];
const DEFAULT_GAS_EMISSION_UNIT = "kgCO2eq/TJ";

/**
 * 서버 emissionFactor.service.js `_deriveAggrGasCode` 와 동일 규칙
 * @param {string} componentCode
 * @returns {'CO2'|'CH4'|'N2O'|'HFCs'|'PFCs'|'SF6'|null}
 */
export function deriveAggrGasCode(componentCode) {
    const code = String(componentCode || "").trim();
    if (!code) return null;
    const upper = code.toUpperCase();
    if (upper === "CO2") return "CO2";
    if (upper === "CH4") return "CH4";
    if (upper === "N2O") return "N2O";
    if (upper === "SF6") return "SF6";
    if (upper === "HFCS" || upper.startsWith("HFC")) return "HFCs";
    if (upper === "PFCS" || upper.startsWith("PFC")) return "PFCs";
    return null;
}

/**
 * 4가스 플랫만 API용 components 행으로 변환
 */
export function buildFourGasComponentsFromFlat(flat, emissionUnit = DEFAULT_GAS_EMISSION_UNIT) {
    const unit = String(emissionUnit || DEFAULT_GAS_EMISSION_UNIT).trim();
    return FOUR_FLAT_GAS_CODES.map((code) => {
        const key = `${code}Factor`;
        const value = Number(flat[key] ?? 0);
        return {
            componentCode: code,
            emissionFactor: Number.isFinite(value) ? value : 0,
            emissionUnit: unit,
        };
    });
}

/**
 * @deprecated 호환용 — buildAllComponents 사용
 */
export function buildGasComponentsFromFlat(flat, emissionUnit = DEFAULT_GAS_EMISSION_UNIT) {
    const unit = String(emissionUnit || DEFAULT_GAS_EMISSION_UNIT).trim();
    const out = buildFourGasComponentsFromFlat(flat, unit);
    const hfc = Number(flat.HFCsFactor ?? 0);
    const pfc = Number(flat.PFCsFactor ?? 0);
    if (Number.isFinite(hfc) && hfc !== 0) {
        out.push({ componentCode: "HFCs", emissionFactor: hfc, emissionUnit: unit });
    }
    if (Number.isFinite(pfc) && pfc !== 0) {
        out.push({ componentCode: "PFCs", emissionFactor: pfc, emissionUnit: unit });
    }
    return out;
}

/**
 * 종별 행에서 componentCode 비어 있지 않은 것만 emissionUnit 과 함께 출력
 * @param {Array<{ componentCode: string, emissionFactor: unknown }>} rows
 * @param {string} emissionUnit
 * @returns {Array<{ componentCode: string, emissionFactor: number, emissionUnit: string }>}
 */
export function speciesRowsToComponents(rows, emissionUnit) {
    const unit = String(emissionUnit || DEFAULT_GAS_EMISSION_UNIT).trim();
    const out = [];
    if (!Array.isArray(rows)) return out;
    for (const row of rows) {
        const code = String(row?.componentCode || "").trim();
        if (!code) continue;
        const v = Number(row?.emissionFactor ?? 0);
        out.push({
            componentCode: code,
            emissionFactor: Number.isFinite(v) ? v : 0,
            emissionUnit: unit,
        });
    }
    return out;
}

/**
 * CO2·CH4·N2O·SF6 플랫 + HFC/PFC 종별 행 + 열량계수 → 전체 components[]
 * @param {Object} flat - CO2Factor, CH4Factor, N2OFactor, SF6Factor
 * @param {Object} [opts]
 * @param {string} [opts.gasEmissionUnit]
 * @param {Array<{componentCode:string, emissionFactor:number}>} [opts.hfcSpeciesRows]
 * @param {Array<{componentCode:string, emissionFactor:number}>} [opts.pfcSpeciesRows]
 * @param {*} [opts.ncvValue] …
 */
export function buildAllComponents(flat, opts = {}) {
    const gasUnit = opts.gasEmissionUnit ?? DEFAULT_GAS_EMISSION_UNIT;
    const out = buildFourGasComponentsFromFlat(flat, gasUnit);
    const hfcParts = speciesRowsToComponents(opts.hfcSpeciesRows, gasUnit);
    const pfcParts = speciesRowsToComponents(opts.pfcSpeciesRows, gasUnit);
    out.push(...hfcParts, ...pfcParts);

    const ncv = opts.ncvValue != null && opts.ncvValue !== "" ? Number(opts.ncvValue) : null;
    const gcv = opts.gcvValue != null && opts.gcvValue !== "" ? Number(opts.gcvValue) : null;
    const ncvUnit = String(opts.ncvUnit || "").trim();
    const gcvUnit = String(opts.gcvUnit || "").trim();
    if (Number.isFinite(ncv) && ncv >= 0 && ncvUnit) {
        out.push({ componentCode: "NCV", emissionFactor: ncv, emissionUnit: ncvUnit });
    }
    if (Number.isFinite(gcv) && gcv >= 0 && gcvUnit) {
        out.push({ componentCode: "GCV", emissionFactor: gcv, emissionUnit: gcvUnit });
    }
    return out;
}

/**
 * components[] 내 가스 componentCode 중복 검사 (NCV/GCV 제외, 빈 코드 제외)
 * @returns {{ ok: true } | { ok: false, duplicate: string }}
 */
export function validateUniqueGasComponentCodes(components) {
    const seen = new Set();
    for (const c of components || []) {
        const code = String(c?.componentCode || "").trim();
        if (!code || code === "NCV" || code === "GCV") continue;
        if (seen.has(code)) return { ok: false, duplicate: code };
        seen.add(code);
    }
    return { ok: true };
}

/**
 * components[]에서 열량계수(NCV, GCV) 값/단위 추출 (표시·편집 폼용)
 */
export function getCalorificFromComponents(item) {
    const components = item?.components;
    const result = { ncv: null, ncvUnit: "", gcv: null, gcvUnit: "" };
    if (!Array.isArray(components)) return result;
    for (const c of components) {
        const code = String(c.componentCode || "").trim();
        if (code === "NCV") {
            result.ncv = Number(c.emissionFactor);
            result.ncvUnit = c.emissionUnit || "";
        } else if (code === "GCV") {
            result.gcv = Number(c.emissionFactor);
            result.gcvUnit = c.emissionUnit || "";
        }
    }
    return result;
}

/**
 * value 또는 factor 객체에서 components 기반 currentValue, displayUnit 유도
 */
export function deriveCurrentValueAndUnit(valueOrFactor) {
    let components = valueOrFactor?.components;
    if (!Array.isArray(components) && valueOrFactor?.factorValues?.[0]?.components) {
        components = valueOrFactor.factorValues[0].components;
    }
    if (!Array.isArray(components) || components.length === 0) {
        const legacy = valueOrFactor?.emissionFactor ?? valueOrFactor?.factorValue;
        const unit = valueOrFactor?.unit ?? valueOrFactor?.emissionUnit ?? "-";
        return {
            currentValue: legacy != null ? Number(legacy) : null,
            displayUnit: unit,
        };
    }
    let sum = 0;
    let displayUnit = "-";
    for (const c of components) {
        const code = String(c.componentCode || "").trim();
        if (code === "NCV" || code === "GCV") continue;
        const aggr = deriveAggrGasCode(code);
        if (aggr && BASKET_GAS_CODES.includes(aggr)) {
            const v = Number(c.emissionFactor);
            if (Number.isFinite(v)) sum += v;
            if (displayUnit === "-" && c.emissionUnit) displayUnit = c.emissionUnit;
        }
    }
    return {
        currentValue: sum,
        displayUnit,
    };
}

/**
 * value 항목에서 집계 바구니별 emissionFactor 합 (종별 HFC-134a 등 → HFCs 합)
 */
export function getGasFactorsFromComponents(item) {
    const map = { CO2: 0, CH4: 0, N2O: 0, HFCs: 0, PFCs: 0, SF6: 0 };
    const components = item?.components;
    if (!Array.isArray(components)) {
        if (item?.CO2Factor != null) map.CO2 = Number(item.CO2Factor);
        if (item?.CH4Factor != null) map.CH4 = Number(item.CH4Factor);
        if (item?.N2OFactor != null) map.N2O = Number(item.N2OFactor);
        if (item?.HFCsFactor != null) map.HFCs = Number(item.HFCsFactor);
        if (item?.PFCsFactor != null) map.PFCs = Number(item.PFCsFactor);
        if (item?.SF6Factor != null) map.SF6 = Number(item.SF6Factor);
        return map;
    }
    for (const c of components) {
        const code = String(c.componentCode || "").trim();
        if (code === "NCV" || code === "GCV") continue;
        const aggr = deriveAggrGasCode(code);
        if (aggr && Object.prototype.hasOwnProperty.call(map, aggr)) {
            map[aggr] += Number(c.emissionFactor) || 0;
        }
    }
    return map;
}

/**
 * factor value의 components[]로 편집 폼용 4가스 + 종별 행 분리
 * @returns {{ CO2Factor, CH4Factor, N2OFactor, SF6Factor, hfcSpeciesRows, pfcSpeciesRows }}
 */
export function splitSpeciesRowsFromComponents(components = []) {
    const hfcSpeciesRows = [];
    const pfcSpeciesRows = [];
    let CO2Factor = null;
    let CH4Factor = 0;
    let N2OFactor = 0;
    let SF6Factor = 0;

    if (!Array.isArray(components)) {
        return {
            CO2Factor: null,
            CH4Factor: 0,
            N2OFactor: 0,
            SF6Factor: 0,
            hfcSpeciesRows: [{ componentCode: "", emissionFactor: null }],
            pfcSpeciesRows: [{ componentCode: "", emissionFactor: null }],
        };
    }

    for (const c of components) {
        const code = String(c.componentCode || "").trim();
        if (!code || code === "NCV" || code === "GCV") continue;
        const aggr = deriveAggrGasCode(code);
        const v = Number(c.emissionFactor);
        const val = Number.isFinite(v) ? v : 0;
        if (aggr === "HFCs") {
            hfcSpeciesRows.push({ componentCode: code, emissionFactor: val });
        } else if (aggr === "PFCs") {
            pfcSpeciesRows.push({ componentCode: code, emissionFactor: val });
        } else if (code === "CO2") {
            CO2Factor = val;
        } else if (code === "CH4") {
            CH4Factor = val;
        } else if (code === "N2O") {
            N2OFactor = val;
        } else if (code === "SF6") {
            SF6Factor = val;
        }
    }

    if (hfcSpeciesRows.length === 0) {
        hfcSpeciesRows.push({ componentCode: "", emissionFactor: null });
    }
    if (pfcSpeciesRows.length === 0) {
        pfcSpeciesRows.push({ componentCode: "", emissionFactor: null });
    }

    return { CO2Factor, CH4Factor, N2OFactor, SF6Factor, hfcSpeciesRows, pfcSpeciesRows };
}

/**
 * HFCs / PFCs 바구니 합이 0보다 큰지 (프론트 detailBasis 검증용)
 */
export function basketPositiveTotals(hfcSpeciesRows, pfcSpeciesRows) {
    let h = 0;
    let p = 0;
    for (const row of hfcSpeciesRows || []) {
        const cc = String(row?.componentCode || "").trim();
        if (!cc || deriveAggrGasCode(cc) !== "HFCs") continue;
        const v = Number(row?.emissionFactor);
        if (Number.isFinite(v) && v > 0) h += v;
    }
    for (const row of pfcSpeciesRows || []) {
        const cc = String(row?.componentCode || "").trim();
        if (!cc || deriveAggrGasCode(cc) !== "PFCs") continue;
        const v = Number(row?.emissionFactor);
        if (Number.isFinite(v) && v > 0) p += v;
    }
    return { hfcs: h, pfcs: p };
}
